import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { RotateCcw, ShoppingBag, ChevronDown, ChevronUp, Package } from "lucide-react";
import { toast } from "sonner";
import { api, formatARS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";

export default function MyOrders() {
  const { user, loading } = useAuth();
  const { addMany } = useCart();
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null);
  const [open, setOpen] = useState({});
  const [repeating, setRepeating] = useState(null);

  useEffect(() => {
    if (user?.role === "customer") api.get("/orders/mine").then((r) => setOrders(r.data)).catch(() => setOrders([]));
  }, [user]);

  if (loading) return <div className="p-20 text-center text-slate-500">Cargando...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "customer") return <Navigate to="/admin" replace />;

  const repeat = async (order) => {
    setRepeating(order.id);
    try {
      const results = await Promise.all(order.items.map((it) => api.get(`/products/${it.product_id}`).then((r) => r.data).catch(() => null)));
      const entries = [];
      const skipped = [];
      results.forEach((p, i) => {
        if (p && p.stock > 0) entries.push({ product: p, quantity: order.items[i].quantity });
        else skipped.push(order.items[i].name);
      });
      if (!entries.length) { toast.error("Ninguno de los productos de este pedido está disponible hoy"); return; }
      addMany(entries);
      toast.success(`${entries.length} producto(s) agregados al carrito con precios actuales`);
      if (skipped.length) toast.warning(`Sin stock o discontinuados: ${skipped.slice(0, 3).join(", ")}${skipped.length > 3 ? "…" : ""}`);
      navigate("/cart");
    } finally {
      setRepeating(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="my-orders-page">
      <span className="text-sm font-semibold uppercase tracking-[0.2em] text-blue-600">Tu cuenta</span>
      <h1 className="font-heading font-extrabold text-3xl md:text-4xl tracking-tight mt-1 mb-2">Mis pedidos</h1>
      <p className="text-slate-500 mb-8">Repetí cualquier pedido anterior con un clic: los productos se cargan al carrito con los precios de hoy.</p>

      {orders === null && <div className="text-center py-16 text-slate-500">Cargando pedidos...</div>}
      {orders && orders.length === 0 && (
        <div className="text-center py-16 bg-white rounded-2xl border border-slate-200" data-testid="my-orders-empty">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto"><Package className="w-8 h-8 text-slate-400" /></div>
          <p className="text-slate-600 mt-4">Todavía no hiciste pedidos.</p>
          <Link to="/shop" className="btn-primary mt-5 inline-flex">Ir a la tienda</Link>
        </div>
      )}
      <div className="space-y-4">
        {orders?.map((o) => {
          const units = o.items.reduce((s, it) => s + it.quantity, 0);
          const expanded = !!open[o.id];
          return (
            <div key={o.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid={`my-order-${o.id}`}>
              <div className="p-5 flex flex-wrap items-center gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="text-xs text-slate-500">{new Date(o.created_at).toLocaleString("es-AR", { dateStyle: "long", timeStyle: "short" })}</div>
                  <div className="font-heading font-bold text-lg mt-0.5">{o.items.length} producto{o.items.length === 1 ? "" : "s"} · {units} unidad{units === 1 ? "" : "es"}</div>
                  {o.seller_name && <div className="text-xs text-blue-700 mt-0.5">Vendedor: {o.seller_code} · {o.seller_name}</div>}
                </div>
                <div className="text-right">
                  <div className="text-xs text-slate-500">Total</div>
                  <div className="font-bold text-xl text-green-700" data-testid={`my-order-total-${o.id}`}>{formatARS(o.total)}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setOpen({ ...open, [o.id]: !expanded })} className="p-2.5 rounded-full border border-slate-200 hover:bg-slate-50 text-slate-600" aria-label="Ver detalle" data-testid={`my-order-toggle-${o.id}`}>
                    {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  <button onClick={() => repeat(o)} disabled={repeating === o.id} className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:bg-slate-300 text-white rounded-full px-5 py-2.5 font-semibold text-sm transition-colors" data-testid={`repeat-order-${o.id}`}>
                    <RotateCcw className="w-4 h-4" /> {repeating === o.id ? "Cargando..." : "Repetir pedido"}
                  </button>
                </div>
              </div>
              {expanded && (
                <div className="border-t border-slate-100 bg-slate-50 px-5 py-3 text-sm" data-testid={`my-order-items-${o.id}`}>
                  {o.items.map((it, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
                      <span className="truncate">{it.code && <span className="font-mono text-xs text-blue-700 mr-2">{it.code}</span>}{it.name}</span>
                      <span className="text-slate-600 whitespace-nowrap ml-4">{it.quantity} × {formatARS(it.price)}</span>
                    </div>
                  ))}
                  {o.notes && <div className="text-xs text-slate-500 mt-2">Notas: {o.notes}</div>}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {orders && orders.length > 0 && (
        <Link to="/shop" className="mt-8 inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"><ShoppingBag className="w-4 h-4" /> Seguir comprando</Link>
      )}
    </div>
  );
}
